<template>
  <div class="form-group" :class="computedClasses">
    <div class="wrapper">
      <label v-if="computedLabel" :for="safeId" @click="onClickLabel">{{
        computedLabel
      }}</label>
      <div v-if="tooltip" :id="tooltipTargetId">
        <slot :id="safeId"></slot>
      </div>
      <slot v-else :id="safeId"></slot>
      <b-tooltip
        v-if="tooltip"
        :target="tooltipTargetId"
        :variant="variant"
        triggers="click blur"
        >{{ tooltip }}</b-tooltip
      >
      <i
        v-if="iconClass"
        class="icon"
        :class="iconClass"
        @click="onClickIcon"
      ></i>
      <i
        v-else-if="errorIcon && errorIconClass && isInvalid"
        class="icon"
        :class="errorIconClass"
      ></i>
    </div>
    <b-form-invalid-feedback v-for="(error, index) in errors" :key="index">{{
      error
    }}</b-form-invalid-feedback>
  </div>
</template>

<script>
import idMixin from "@/mixins/forms/id";
import stateMixin from "@/mixins/forms/state";
import disableMixin from "@/mixins/forms/disable";
import focusMixin from "@/mixins/forms/focus";
import variantMixin from "@/mixins/forms/variant";
import sizeMixin from "@/mixins/forms/size";
import placeholderMixin from "@/mixins/forms/placeholder";
import tooltipMixin from "@/mixins/forms/tooltip";

export default {
  name: "AppFormGroup",
  mixins: [
    idMixin,
    stateMixin,
    disableMixin,
    variantMixin,
    focusMixin,
    sizeMixin,
    placeholderMixin,
    tooltipMixin,
  ],
  props: {
    label: {
      type: String,
      required: false,
      default: null,
    },
    iconClass: {
      type: String,
      required: false,
      default: null,
    },
    errorIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
    errorIconClass: {
      type: String,
      required: false,
      default: "icon-info",
    },
    focused: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  provide() {
    return {
      formGroup: this,
    };
  },
  computed: {
    computedClasses() {
      return {
        ...this.disabledClass,
        ...this.sizeClass,
        ...this.variantClass,
        ...this.focusClass,
        ...this.stateClass,
        ...this.placeholderClass,
        ...this.tooltipClass,
      };
    },
    sizeClass() {
      return { [`form-group-${this.size}`]: this.size };
    },
    computedLabel() {
      return this.label || this.placeholder;
    },
  },
  methods: {
    onClickIcon() {
      if (this.disabled) {
        return;
      }

      this.$emit("clickIcon");
    },
    onClickLabel() {
      this.$slots.default.forEach(slot => {
        if (typeof slot.componentInstance.focus === "function") {
          slot.componentInstance.focus();
        }
      });
      this.focused_ = true;
    },
  },
  watch: {
    focused: {
      handler(value) {
        this.focused_ = value;
      },
      immediate: true,
    },
  },
  data() {
    return {
      tooltipTargetId: `tooltip-target-${this._uid}`,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-group {
  .wrapper {
    position: relative;
    width: 100%;
  }

  ::v-deep {
    .form-control {
      margin-bottom: 0;
      font-size: 1rem;
      padding-top: 1.5rem;
      background-color: $white;
      color: rgba(theme-get("small", "primary-darken-3"), 0.7);
      @include themify($themes) {
        color: rgba(themed("primary-darken-3"), 0.7);
      }
      @include autofill {
        box-shadow: 0 0 0 30px white inset !important;
      }
    }
    .custom-radio,
    .custom-checkbox {
      padding-left: 0;
      cursor: pointer;
      .custom-control-label {
        line-height: 1.3rem;
        vertical-align: middle;
        font-size: 0.875rem;
        padding-left: 1.875rem;
        cursor: pointer;
        color: rgba(theme-get("small", "primary-darken-3"), 0.7);
        @include themify($themes) {
          color: rgba(themed("primary-darken-3"), 0.7);
        }
        &:before {
          top: 0;
          width: 1.25rem;
          left: 0;
          height: 1.25rem;
          background-color: $white;
          margin-right: 0.5rem;
          border: 1px solid theme-get("small", "primary-lighten-4");
          @include themify($themes) {
            border: 1px solid themed("primary-lighten-4");
          }
        }
        &:after {
          background-color: theme-get("small", "primary");
          border-radius: 50%;
          width: 0.75rem;
          left: 0.25rem;
          height: 0.75rem;
          display: none;
          @include themify($themes) {
            background-color: themed("primary");
          }
        }
      }
      .custom-control-input {
        &:checked {
          & + .custom-control-label {
            &:after {
              display: block;
            }
          }
        }
      }
    }
    .custom-checkbox {
      .custom-control-label {
        &:before {
          border-radius: 0.25rem;
        }
        &:after {
          border-radius: 0.125rem;
        }
      }
    }
    .flatpickr-day {
      &.selected {
        background-color: theme-get("small", "primary") !important;
        border-color: theme-get("small", "primary") !important;
        @include themify($themes) {
          background-color: themed("primary") !important;
          border-color: themed("primary") !important;
        }
      }
    }
  }

  select {
    margin-bottom: 0;
    font-size: 1rem;
    padding-top: 1rem;
  }

  label {
    position: absolute;
    z-index: 4;
    left: 0.875rem;
    margin-bottom: 0;
    font-size: 0.75rem;
    top: 0.25rem;
    margin-top: 0;
    transition: 0.1s all ease-in-out;
    cursor: text;
    color: rgba(theme-get("small", "primary-darken-3"), 0.7);
    @include themify($themes) {
      color: rgba(themed("primary-darken-3"), 0.7);
    }
    @include translateY(0);
  }

  .icon {
    position: absolute;
    right: 0.5rem;
    padding: 0.4375rem;
    z-index: 4;
    width: 2.5rem;
    text-align: center;
    font-size: 1.25rem;
    top: 50%;
    @include translateY(-50%);
    color: $black;
  }

  &.has-placeholder {
    label {
      top: 50%;
      font-size: 1rem;
      @include translateY(-50%);
    }
    .form-control {
      padding-top: 0.5rem;
    }
  }

  &.is-focused {
    label {
      font-size: 0.75rem;
      top: 0.25rem;
      margin-top: 0;
      @include translateY(0);
    }
    .form-control {
      padding-top: 1.5rem;
    }
  }

  &.is-invalid {
    &::v-deep {
      .form-control,
      .custom-select {
        color: $danger;
        border-color: $red-dark;
        background-color: rgba($danger, 0.04);
        @include placeholder {
          color: $danger;
        }
      }
      .custom-radio,
      .custom-checkbox {
        .custom-control-label {
          &:before {
            background-color: rgba($danger, 0.04);
            border-color: $red-dark;
          }
          &:after {
            background-color: $red-dark;
            @include themify($themes) {
              background-color: $red-dark;
            }
          }
        }
      }
    }
    label,
    .icon {
      color: $danger !important;
    }
    .invalid-feedback {
      display: block;
      font-size: 0.75rem;
      padding-left: 1rem;
      i {
        margin-right: 0.125rem;
      }
    }
  }

  &.is-disabled {
    &::v-deep {
      .form-control,
      .custom-select {
        cursor: not-allowed;
        background-color: $input-disabled-bg !important;
        color: rgba(theme-get("small", "primary-darken-3"), 0.7);
        @include themify($themes) {
          background-color: themed("primary-lighten-5") !important;
          color: rgba(themed("primary-darken-3"), 0.7);
        }
      }
      .custom-radio,
      .custom-checkbox {
        .custom-control-label {
          &:before {
            border-color: $input-disabled-bg;
            background-color: transparent !important;
            @include themify($themes) {
              border-color: themed("primary-lighten-5");
            }
          }
          &:after {
            background-color: $input-disabled-bg;
            @include themify($themes) {
              background-color: themed("primary-lighten-5");
            }
          }
        }
      }
    }
    .icon {
      cursor: not-allowed;
    }
  }

  &.form-group--checkbox {
    .invalid-feedback {
      padding-left: 1.875rem;
    }
    &.is-invalid {
      padding-right: 2.5rem;
      .icon {
        right: -2rem;
      }
    }
  }

  &.form-variant {
    &-white {
      &::v-deep {
        .form-control,
        .custom-select {
          color: $white;
          border-color: rgba($white, 0.4);
          background-color: transparent;
          @include placeholder {
            color: rgba($white, 0.7);
          }
          @include autofill {
            -webkit-box-shadow: 0 0 0 30px $finch-darken-2 inset !important;
            @include themify($themes) {
              -webkit-box-shadow: 0 0 0 30px themed("primary-darken-2") inset !important;
            }
            -webkit-text-fill-color: $white !important;
          }
        }
        option {
          color: $black;
        }
        .col-form-label {
          color: $white;
        }
        .custom-radio,
        .custom-checkbox {
          .custom-control-label {
            color: $white !important;
            &:before {
              background-color: transparent;
              border: 1px solid rgba($white, 0.5) !important;
            }
            &:after {
              background-color: $white !important;
            }
          }
        }
      }

      &.is-invalid {
        ::v-deep {
          .form-control,
          .custom-select {
            border: none !important;
            color: $danger;
            background-color: $white;
            @include placeholder {
              color: $danger;
            }
          }
        }
        .invalid-feedback {
          color: $white;
        }
        .icon {
          color: rgba($white, 0.2);
        }
        &.form-group--checkbox {
          .icon {
            background-color: $white;
            border-radius: 50%;
            padding: 0.25rem;
            width: auto;
            height: auto;
            line-height: 1;
          }
          &::v-deep {
            .custom-control-label {
              &:after {
                display: block;
                left: 0;
                top: 0;
                width: 1.25rem;
                height: 1.25rem;
              }
            }
          }
        }
      }

      &.is-disabled {
        background-color: transparent !important;
        border-color: rgba($white, 0.25);
        color: rgba($white, 0.5);
        .icon {
          color: rgba($white, 0.7);
        }
        &::v-deep {
          .form-control,
          .custom-select {
            background-color: transparent !important;
            color: rgba($white, 0.5) !important;
          }
          .custom-radio,
          .custom-checkbox {
            .custom-control-label {
              &:before {
                border: 1px solid rgba($white, 0.2) !important;
              }
              &:after {
                background-color: rgba($white, 0.2) !important;
              }
            }
          }
        }
      }

      label {
        color: rgba($white, 0.7);
      }
      .icon {
        color: $white;
      }
    }
  }
}
</style>
